import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { SalaryRow } from '@chronos/shared';
import { useSalaryMutations, useSalarySheet } from '@/hooks/queries';
import { Card, ConfirmButton, EmptyState, ErrorNote, Modal, PageHeader, Spinner, StatTile, SubmitButton } from '@/components/ui';
import { MonthNav, currentMonthKey, monthLabel } from '@/pages/AttendancePage';
import { formatDateTime } from '@/lib/format';

/** Signed days: a grant may be negative, so "+-5" must not happen. */
const signed = (n: number) => `${n > 0 ? '+' : ''}${n}`;

function useMoney(currency: string) {
  return useMemo(() => {
    const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 });
    return (n: number | null | undefined) => (n === null || n === undefined ? '—' : fmt.format(n));
  }, [currency]);
}

/** Admin-only. Every figure is computed from attendance; only salaries and adjustments are stored. */
export function SalarySheetPage() {
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? currentMonthKey();
  const { data, isLoading, error } = useSalarySheet(month);
  const { setSalary } = useSalaryMutations();
  const [editing, setEditing] = useState<SalaryRow | null>(null);
  const [adjusting, setAdjusting] = useState<SalaryRow | null>(null);
  const [banking, setBanking] = useState<SalaryRow | null>(null);

  const currency = data?.currency ?? 'INR';
  const money = useMoney(currency);
  const rows = data?.rows ?? [];
  const priced = rows.filter((r) => r.monthlySalary !== null);
  const sum = (f: (r: SalaryRow) => number | null) => priced.reduce((s, r) => s + (f(r) ?? 0), 0);
  const projected = rows.some((r) => r.projected);

  const exportCsv = () => {
    const header = ['Employee', 'Joined', 'Monthly salary', 'Gross (pro-rated)', 'Working days', 'Per-day rate', 'Present', 'Half', 'Leaves', 'Adjustments', 'Leave units', 'Approved units', 'Unapproved units', 'Net deficit (h)', 'Deficit leave', 'Bank opening', 'Accrued', 'Granted', 'Bank used', 'Bank closing', 'Unpaid', 'Deduction', 'Late', 'No-break days', 'Bonus', 'Leave encashment', 'Net payable'];
    const lines = [header, ...rows.map((r) => [
      r.userName, r.joinedOn ?? '', r.monthlySalary ?? '', r.grossPay ?? '', r.workingDays, r.perDayRate ?? '', r.presentDays, r.halfDays, r.leaveDays, r.adjustmentDays,
      r.leaveUnits, r.approvedLeaveUnits, r.unapprovedLeaveUnits, r.netDeficitHours, r.deficitLeaveUnits, r.bank.opening, r.bank.accrued, r.bank.granted, r.bank.used, r.bank.closing, r.unpaidLeaveUnits, r.deduction ?? '', r.lateDays, r.noBreakDays, r.bonus, r.encashment ?? '', r.netPayable ?? '',
    ])].map((cells) => cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([`﻿${lines}\r\n`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `salary-${month}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Salary sheet"
        description={`${monthLabel(month)} · computed from attendance. Set each employee's monthly salary, then adjust leaves where the system and reality disagree.`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-secondary" onClick={exportCsv} disabled={rows.length === 0}>Export CSV</button>
            <MonthNav month={month} onChange={(m) => setParams((p) => { p.set('month', m); return p; })} />
          </div>
        }
      />

      {projected && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          This month is not over — leaves, deductions and bonuses will keep changing until it is. Past months are final.
        </div>
      )}

      <ErrorNote error={error ?? setSalary.error} />

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : rows.length === 0 ? (
        <Card><EmptyState title="No active employees" /></Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
            <StatTile label="Employees" value={String(rows.length)} hint={`${priced.length} with a salary set`} />
            <StatTile label="Gross" value={money(sum((r) => r.grossPay))} hint="pro-rated for joiners" />
            <StatTile label="Deductions" value={money(sum((r) => r.deduction))} hint="unpaid leave" />
            <StatTile label="Bonuses" value={money(sum((r) => r.bonus))} hint="attendance" tone="green" />
            {rows.some((r) => r.bank.encashed) ? (
              <StatTile label="Leave encashment" value={money(sum((r) => r.encashment))} hint="year-end payout" tone="green" />
            ) : (
              <StatTile label="Leave bank" value={String(rows.reduce((s, r) => s + r.bank.closing, 0))} hint="days carried forward" />
            )}
            {sum((r) => r.netDeficitHours) > 0 && (
              <StatTile
                label="Total deficit"
                value={`${Math.round(sum((r) => r.netDeficitHours) * 10) / 10}h`}
                hint={`${Math.round(sum((r) => r.deficitLeaveUnits) * 10) / 10} day(s) of leave charged`}
                tone="rose"
              />
            )}
            <StatTile label="Net payable" value={money(sum((r) => r.netPayable))} tone="brand" />
          </div>

          <Card className="mt-4 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="th">Employee</th>
                    <th className="th text-right">Salary</th>
                    <th className="th text-right">Per day</th>
                    <th className="th text-right" title="Present / Half / Late / No-break">P · H · L · NB</th>
                    <th className="th text-right">Leaves</th>
                    <th className="th text-right">Adj.</th>
                    <th className="th text-right" title="Leave units: approved / unapproved">Units</th>
                    <th className="th text-right" title="Net short hours for the month, and the leave they convert to">Deficit</th>
                    <th className="th text-right" title="Leave bank: opening → closing this month">Bank</th>
                    <th className="th text-right">Paid</th>
                    <th className="th text-right">Unpaid</th>
                    <th className="th text-right">Deduction</th>
                    <th className="th text-right">Bonus</th>
                    <th className="th text-right" title="Year-end leave payout">Encash</th>
                    <th className="th text-right">Net</th>
                    <th className="th"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.userId} className="hover:bg-slate-50">
                      <td className="td font-medium text-slate-900">
                        <Link to={`/attendance?userId=${r.userId}&month=${month}`} className="text-brand-700 hover:underline">{r.userName}</Link>
                      </td>
                      <td className="td text-right font-mono tabular-nums">
                        <button className="rounded px-1 hover:bg-slate-100" onClick={() => setEditing(r)} title={`Set monthly salary / joining date${r.joinedOn ? ` (joined ${r.joinedOn})` : ''}`}>
                          {r.monthlySalary === null ? <span className="text-amber-700">set…</span> : money(r.monthlySalary)}
                        </button>
                        {r.grossPay !== null && r.monthlySalary !== null && r.grossPay !== r.monthlySalary && (
                          <p className="text-[10px] text-slate-500" title="Pro-rated: joined mid-month">→ {money(r.grossPay)}</p>
                        )}
                      </td>
                      <td className="td text-right font-mono tabular-nums text-slate-500">{money(r.perDayRate)}</td>
                      <td className="td text-right tabular-nums text-xs text-slate-500">
                        <span className="text-emerald-700">{r.presentDays}</span> · <span className="text-amber-700">{r.halfDays}</span> ·{' '}
                        <span className={r.lateDays ? 'text-rose-700' : ''}>{r.lateDays}</span> · <span className={r.noBreakDays ? 'text-amber-700' : ''}>{r.noBreakDays}</span>
                      </td>
                      <td className={`td text-right tabular-nums ${r.leaveDays ? 'text-rose-700' : 'text-slate-500'}`}>{r.leaveDays}</td>
                      <td className={`td text-right tabular-nums ${r.adjustmentDays ? 'font-semibold text-brand-700' : 'text-slate-400'}`}>
                        {r.adjustmentDays > 0 ? '+' : ''}{r.adjustmentDays || '—'}
                      </td>
                      <td className="td text-right tabular-nums">
                        <span className="font-semibold">{r.leaveUnits}</span>
                        {r.leaveUnits > 0 && (
                          <p className="text-[10px]" title="approved / unapproved">
                            <span className="text-emerald-700">{r.approvedLeaveUnits}</span> / <span className={r.unapprovedLeaveUnits ? 'font-semibold text-rose-700' : 'text-slate-400'}>{r.unapprovedLeaveUnits}</span>
                          </p>
                        )}
                      </td>
                      <td className={`td text-right tabular-nums text-xs ${r.netDeficitHours > 0 ? 'bg-pink-100' : ''}`}>
                        {r.netDeficitHours > 0 ? (
                          <>
                            <span className="font-mono font-semibold text-pink-800">{r.netDeficitHours}h</span>
                            {r.deficitLeaveUnits > 0 && (
                              <p className="text-[10px] font-semibold text-rose-600" title="Charged as leave, but never taken from the leave bank">
                                {r.deficitLeaveUnits} day
                              </p>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="td text-right tabular-nums">
                        <button className="rounded px-1 font-mono text-xs hover:bg-slate-100" onClick={() => setBanking(r)} title={`Opening ${r.bank.opening} + ${r.bank.accrued} accrued${r.bank.granted ? ` + ${r.bank.granted} granted` : ''} − ${r.bank.used} used = ${r.bank.closing}`}>
                          {r.bank.opening} → <b>{r.bank.closing}</b>{r.bank.granted ? <span className="text-brand-700"> +{r.bank.granted}</span> : null}
                        </button>
                      </td>
                      <td className="td text-right tabular-nums text-slate-500">{r.paidLeavesUsed}</td>
                      <td className={`td text-right tabular-nums ${r.unpaidLeaveUnits ? 'text-rose-700' : 'text-slate-500'}`}>{r.unpaidLeaveUnits}</td>
                      <td className={`td text-right font-mono tabular-nums ${r.deduction ? 'text-rose-700' : 'text-slate-500'}`}>{r.deduction ? `−${money(r.deduction)}` : money(r.deduction)}</td>
                      <td className={`td text-right font-mono tabular-nums ${r.bonus ? 'text-emerald-700' : 'text-slate-500'}`}>{r.bonus ? `+${money(r.bonus)}` : money(0)}</td>
                      <td className={`td text-right font-mono tabular-nums ${r.encashment ? 'text-emerald-700' : 'text-slate-400'}`} title={r.bank.encashed ? `${r.bank.closing} days × ${money(r.perDayRate)}` : 'Paid out in the year-end month'}>
                        {r.encashment ? `+${money(r.encashment)}` : '—'}
                      </td>
                      <td className="td text-right font-mono font-semibold tabular-nums">{money(r.netPayable)}</td>
                      <td className="td">
                        <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setAdjusting(r)}>
                          Adjust{r.adjustments.length ? ` (${r.adjustments.length})` : ''}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="mt-3 text-xs text-slate-400">
            Units = leaves + ½ × half days + adjustments + deficit leave, shown as approved / unapproved. Deficit leave is deducted but never drawn from the bank. Only <b>approved</b> leave (an approved application, or a
            positive admin adjustment) draws from the <b>bank</b>; unapproved absence is deducted at salary ÷ working days however many days
            are banked. The bank carries forward until the year-end month, when the balance is paid out. Bonus: 0 units → full, ≤ 1 → reduced,
            more → none. Configure in <Link to="/salary/settings" className="text-brand-700 hover:underline">Salary settings</Link>.
          </p>
        </>
      )}

      {editing && <SalaryModal key={editing.userId} row={editing} currency={currency} onClose={() => setEditing(null)} />}
      {banking && (
        <BankModal
          key={`${banking.userId}-${banking.bank.grants.length}`}
          row={rows.find((r) => r.userId === banking.userId) ?? banking}
          month={month}
          onClose={() => setBanking(null)}
        />
      )}
      {adjusting && (
        <AdjustModal
          key={`${adjusting.userId}-${adjusting.adjustments.length}`}
          row={rows.find((r) => r.userId === adjusting.userId) ?? adjusting}
          month={month}
          onClose={() => setAdjusting(null)}
        />
      )}
    </div>
  );
}

function SalaryModal({ row, currency, onClose }: { row: SalaryRow; currency: string; onClose: () => void }) {
  const { setSalary } = useSalaryMutations();
  const [value, setValue] = useState(row.monthlySalary === null ? '' : String(row.monthlySalary));
  const [joinedOn, setJoinedOn] = useState(row.joinedOn ?? '');
  return (
    <Modal
      open
      title={`Monthly salary — ${row.userName}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <SubmitButton form="salary-form" pending={setSalary.isPending} className="btn-primary">Save</SubmitButton>
        </>
      }
    >
      <form
        id="salary-form"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setSalary.mutate({ userId: row.userId, monthlySalary: value === '' ? null : Number(value), joinedOn: joinedOn || null }, { onSuccess: onClose });
        }}
      >
        <ErrorNote error={setSalary.error} />
        <div>
          <label className="label" htmlFor="sal">Gross monthly salary ({currency})</label>
          <input id="sal" type="number" min={0} step={1} className="input" autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder="Leave blank to unset" />
        </div>
        <div>
          <label className="label" htmlFor="joined">Joined on</label>
          <input id="joined" type="date" className="input" value={joinedOn} onChange={(e) => setJoinedOn(e.target.value)} />
          <p className="mt-1 text-xs text-slate-500">
            Attendance and leave accrual start here; earlier days are not leaves, and the joining month is paid pro rata. For staff who predate
            TickTock, use the day you started tracking here — otherwise every earlier month in the year counts as leave. Blank = account creation date.
          </p>
        </div>
        <p className="text-xs text-slate-500">Applies to every month it is viewed in. Changes are audited.</p>
      </form>
    </Modal>
  );
}

function BankModal({ row, month, onClose }: { row: SalaryRow; month: string; onClose: () => void }) {
  const { addBankGrant, removeBankGrant } = useSalaryMutations();
  const [days, setDays] = useState('1');
  const [reason, setReason] = useState('');
  const b = row.bank;
  return (
    <Modal open wide title={`Leave bank — ${row.userName} · ${monthLabel(month)}`} onClose={onClose} footer={<button className="btn-secondary" onClick={onClose}>Done</button>}>
      <div className="grid grid-cols-5 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-center text-xs">
        <div><p className="text-slate-500">Opening</p><p className="text-base font-semibold">{b.opening}</p></div>
        <div><p className="text-slate-500">Accrued</p><p className="text-base font-semibold text-emerald-700">+{b.accrued}</p></div>
        <div><p className="text-slate-500">Granted</p><p className={`text-base font-semibold ${b.granted ? 'text-brand-700' : 'text-slate-400'}`}>{b.granted ? signed(b.granted) : '0'}</p></div>
        <div><p className="text-slate-500">Used</p><p className={`text-base font-semibold ${b.used ? 'text-rose-700' : 'text-slate-400'}`}>{b.used ? `−${b.used}` : '0'}</p></div>
        <div><p className="text-slate-500">Closing</p><p className="text-base font-semibold">{b.closing}</p></div>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        {b.encashed
          ? `Year-end month: the closing balance is paid out${row.encashment ? ` (${row.encashment.toLocaleString()})` : ''} and the bank restarts next month.`
          : 'Unused days carry forward each month until the year-end month, when the balance is paid out at the per-day rate.'}
      </p>

      <form
        className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          const d = Number(days);
          if (!d || !reason.trim()) return;
          addBankGrant.mutate({ userId: row.userId, month, days: d, reason: reason.trim() }, { onSuccess: () => setReason('') });
        }}
      >
        <div>
          <label className="label" htmlFor="bank-days">Grant days</label>
          <input id="bank-days" type="number" step={0.5} min={-365} max={365} className="input max-w-[6rem]" value={days} onChange={(e) => setDays(e.target.value)} />
        </div>
        <div className="min-w-[14rem] flex-1">
          <label className="label" htmlFor="bank-reason">Reason</label>
          <input id="bank-reason" className="input" required maxLength={200} placeholder="e.g. Opening balance from previous system, comp-off for release weekend" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <SubmitButton pending={addBankGrant.isPending} className="btn-primary">Add to bank</SubmitButton>
      </form>
      <p className="mt-1 text-xs text-slate-500">Lands in {monthLabel(month)} and carries forward from there. Negative removes days.</p>
      <ErrorNote error={addBankGrant.error ?? removeBankGrant.error} />

      {b.grants.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No grants this month.</p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
          {b.grants.map((g) => (
            <li key={g.id} className="flex items-center gap-3 px-3 py-2">
              <span className={`w-12 text-right font-mono text-sm font-semibold tabular-nums ${g.days > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{g.days > 0 ? '+' : ''}{g.days}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{g.reason}</span>
              <span className="text-xs text-slate-400">{g.createdByName ?? '—'} · {formatDateTime(g.createdAt)}</span>
              <ConfirmButton className="btn-ghost px-2 py-1 text-xs text-rose-600" label="Remove" confirmLabel="Confirm" onConfirm={() => removeBankGrant.mutate(g.id)} />
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function AdjustModal({ row, month, onClose }: { row: SalaryRow; month: string; onClose: () => void }) {
  const { addAdjustment, removeAdjustment } = useSalaryMutations();
  const [days, setDays] = useState('1');
  const [reason, setReason] = useState('');
  return (
    <Modal
      open
      wide
      title={`Leave adjustments — ${row.userName} · ${monthLabel(month)}`}
      onClose={onClose}
      footer={<button className="btn-secondary" onClick={onClose}>Done</button>}
    >
      <p className="text-sm text-slate-600">
        The system counted <b>{row.leaveDays}</b> leave day{row.leaveDays === 1 ? '' : 's'} and <b>{row.halfDays}</b> half day{row.halfDays === 1 ? '' : 's'} from tracked time.
        Add a correction when that is not what actually happened: <b>+1</b> for an approved leave the system saw as a working day,
        <b> −1</b> for a day worked but not tracked, <b>±0.5</b> for half days.
      </p>

      <form
        className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          const d = Number(days);
          if (!d || !reason.trim()) return;
          addAdjustment.mutate({ userId: row.userId, month, days: d, reason: reason.trim() }, { onSuccess: () => { setReason(''); } });
        }}
      >
        <div>
          <label className="label" htmlFor="adj-days">Days</label>
          <select id="adj-days" className="input" value={days} onChange={(e) => setDays(e.target.value)}>
            {['-2', '-1.5', '-1', '-0.5', '0.5', '1', '1.5', '2', '3'].map((v) => <option key={v} value={v}>{Number(v) > 0 ? `+${v}` : v}</option>)}
          </select>
        </div>
        <div className="min-w-[14rem] flex-1">
          <label className="label" htmlFor="adj-reason">Reason</label>
          <input id="adj-reason" className="input" required maxLength={200} placeholder="e.g. Worked on the 12th but did not track it" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <SubmitButton pending={addAdjustment.isPending} className="btn-primary">Add</SubmitButton>
      </form>
      <ErrorNote error={addAdjustment.error ?? removeAdjustment.error} />

      {row.adjustments.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No adjustments this month.</p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
          {row.adjustments.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2">
              <span className={`w-12 text-right font-mono text-sm font-semibold tabular-nums ${a.days > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                {a.days > 0 ? '+' : ''}{a.days}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{a.reason}</span>
              <span className="text-xs text-slate-400">{a.createdByName ?? '—'} · {formatDateTime(a.createdAt)}</span>
              <ConfirmButton className="btn-ghost px-2 py-1 text-xs text-rose-600" label="Remove" confirmLabel="Confirm" onConfirm={() => removeAdjustment.mutate(a.id)} />
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 grid grid-cols-4 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-center text-xs">
        <div><p className="text-slate-500">Units</p><p className="text-base font-semibold">{row.leaveUnits}</p></div>
        <div><p className="text-slate-500">Paid</p><p className="text-base font-semibold">{row.paidLeavesUsed}</p></div>
        <div><p className="text-slate-500">Unpaid</p><p className="text-base font-semibold text-rose-700">{row.unpaidLeaveUnits}</p></div>
        <div><p className="text-slate-500">Bonus</p><p className="text-base font-semibold text-emerald-700">{row.bonus}</p></div>
      </div>
    </Modal>
  );
}
